import React, { useState } from 'react';
import { MinimalistFinanceIcon, LogoIcon } from './icons';

interface OnboardingWizardProps {
    onComplete: (config: { currency: string, profile: string, alias: string }) => void;
}

const currencies = [
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'USD', name: 'Dólar Americano', symbol: '$' },
    { code: 'MXN', name: 'Peso Mexicano', symbol: '$' },
    { code: 'COP', name: 'Peso Colombiano', symbol: '$' },
    { code: 'ARS', name: 'Peso Argentino', symbol: '$' },
];

const profiles = [
    { id: 'personal', name: 'Personal', description: 'Para gestionar mis finanzas del día a día.' },
    { id: 'work', name: 'Trabajo / Autónomo', description: 'Para seguir los ingresos y gastos de mi negocio.' },
    { id: 'family', name: 'Familia', description: 'Para controlar las finanzas del hogar y la familia.' },
    { id: 'all', name: 'Todo en uno', description: 'Combinar mis finanzas personales y de trabajo.' },
];

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ onComplete }) => {
    const [step, setStep] = useState(1);
    const [alias, setAlias] = useState('');
    const [currency, setCurrency] = useState('EUR');
    const [profile, setProfile] = useState('');

    const handleNext = () => setStep(prev => prev + 1);
    const handleBack = () => setStep(prev => prev - 1);
    
    const handleComplete = () => {
        if(currency && profile && alias){
            onComplete({ currency, profile, alias });
        } else {
            // Failsafe, should not happen with disabled buttons
            alert("Por favor, completa todos los pasos.");
        }
    };
    
    const handleSkip = () => {
         onComplete({ currency: 'EUR', profile: 'personal', alias: 'Usuario' });
    }

    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <div className="text-center">
                        <LogoIcon className="w-24 h-24 text-primary mx-auto mb-6" />
                        <h1 className="text-3xl font-bold text-light-text-primary dark:text-text-primary">¡Bienvenido/a a Mis Cuentas 360!</h1>
                        <p className="mt-4 text-light-text-secondary dark:text-text-secondary">Vamos a realizar una configuración rápida para personalizar tu experiencia. Es solo un momento.</p>
                        <div className="mt-8 flex justify-center gap-4">
                            <button onClick={handleSkip} className="px-6 py-2 rounded-lg text-primary hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">Saltar</button>
                            <button onClick={handleNext} className="px-6 py-2 rounded-lg bg-primary text-white font-semibold hover:bg-blue-600 transition-colors">Empezar</button>
                        </div>
                    </div>
                );
            case 2:
                return (
                     <div>
                        <h2 className="text-2xl font-bold text-center text-light-text-primary dark:text-text-primary mb-6">¿Cómo te llamas?</h2>
                        <p className="text-center text-sm text-light-text-secondary dark:text-text-secondary mb-6">Este nombre se mostrará en tu perfil.</p>
                        <input
                            type="text"
                            value={alias}
                            onChange={(e) => setAlias(e.target.value)}
                            placeholder="Introduce tu nombre o alias"
                            className="w-full text-center bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-primary"
                            autoFocus
                        />
                         <div className="mt-8 flex justify-between">
                            <button onClick={handleBack} className="px-6 py-2 rounded-lg text-primary hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">Atrás</button>
                            <button onClick={handleNext} disabled={!alias.trim()} className="px-6 py-2 rounded-lg bg-primary text-white font-semibold hover:bg-blue-600 transition-colors disabled:bg-gray-500 disabled:cursor-not-allowed">Siguiente</button>
                        </div>
                    </div>
                );
            case 3:
                 return (
                    <div>
                        <h2 className="text-2xl font-bold text-center text-light-text-primary dark:text-text-primary mb-6">Selecciona tu moneda principal</h2>
                        <div className="space-y-3">
                            {currencies.map(c => (
                                <div key={c.code} onClick={() => setCurrency(c.code)} className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${currency === c.code ? 'bg-primary/10 border-primary ring-2 ring-primary' : 'bg-gray-100 dark:bg-gray-800 border-light-border dark:border-border hover:border-primary/50'}`}>
                                    <p className="font-semibold text-light-text-primary dark:text-text-primary">{c.symbol} {c.name}</p>
                                    <p className="text-sm text-light-text-secondary dark:text-text-secondary">{c.code}</p>
                                </div>
                            ))}
                        </div>
                         <div className="mt-8 flex justify-between">
                            <button onClick={handleBack} className="px-6 py-2 rounded-lg text-primary hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">Atrás</button>
                            <button onClick={handleNext} className="px-6 py-2 rounded-lg bg-primary text-white font-semibold hover:bg-blue-600 transition-colors">Siguiente</button>
                        </div>
                    </div>
                );
            case 4:
                return (
                    <div>
                        <h2 className="text-2xl font-bold text-center text-light-text-primary dark:text-text-primary mb-6">¿Para qué usarás la aplicación?</h2>
                        <div className="space-y-3">
                             {profiles.map(p => (
                                <div key={p.id} onClick={() => setProfile(p.id)} className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${profile === p.id ? 'bg-primary/10 border-primary ring-2 ring-primary' : 'bg-gray-100 dark:bg-gray-800 border-light-border dark:border-border hover:border-primary/50'}`}>
                                    <p className="font-semibold text-light-text-primary dark:text-text-primary">{p.name}</p>
                                    <p className="text-sm text-light-text-secondary dark:text-text-secondary">{p.description}</p>
                                </div>
                            ))}
                        </div>
                        <div className="mt-8 flex justify-between">
                            <button onClick={handleBack} className="px-6 py-2 rounded-lg text-primary hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">Atrás</button>
                            <button onClick={handleNext} disabled={!profile} className="px-6 py-2 rounded-lg bg-primary text-white font-semibold hover:bg-blue-600 transition-colors disabled:bg-gray-500 disabled:cursor-not-allowed">Siguiente</button>
                        </div>
                    </div>
                );
            case 5:
                 return (
                    <div className="text-center">
                        <MinimalistFinanceIcon className="w-24 h-24 text-secondary mx-auto mb-6" />
                        <h1 className="text-3xl font-bold text-light-text-primary dark:text-text-primary">¡Todo listo, {alias}!</h1>
                        <p className="mt-4 text-light-text-secondary dark:text-text-secondary">Hemos configurado la aplicación para ti. Ya puedes empezar a tomar el control de tus finanzas.</p>
                        <div className="mt-8 flex justify-center">
                            <button onClick={handleComplete} className="px-8 py-3 text-lg rounded-lg bg-secondary text-white font-semibold hover:bg-green-600 transition-colors shadow-lg">Entrar en la App</button>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };
    
    return (
        <div className="flex items-center justify-center min-h-screen bg-light-background dark:bg-background p-4">
            <div className="w-full max-w-lg bg-light-surface dark:bg-surface rounded-xl shadow-2xl border border-light-border dark:border-border p-8 sm:p-12">
                {renderStep()}
            </div>
        </div>
    );
};

export default OnboardingWizard;